import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {
  Popover,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  useTheme,
} from '@mui/material';
import { useMentionSearch } from '../../hooks/useMentionSearch';
import { getMemeverseUsers } from '../../utils/apis/memeverse';
import { useAuth } from '../../hooks/useAuth';
import { UserDetailType } from './memeverse_types';

interface CommentInputProps {
  defaultComment?: string;
  onChange?: (value: string) => void;
}

export const CommentInput = forwardRef(({ defaultComment, onChange }: CommentInputProps, ref) => {
  const [comment, setComment] = useState(defaultComment);
  const [suggestions, setSuggestions] = useState<UserDetailType[]>([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  const inputRef = useRef(null);
  const mirrorRef = useRef(null);
  const mentions = useRef({});

  const { authUser } = useAuth();
  const theme = useTheme();

  const { markMentionInserted } = useMentionSearch(comment, async (term) => {
    getMemeverseUsers(authUser?.uid, term, 1, 10)
      .then((resp) => {
        if (resp.ok) {
          return resp.json();
        }
      })
      .then((data) => {
        setSuggestions(data.data as UserDetailType[])
      })
      .catch((err) => {
        console.log('Something went with search: ', err)
        setSuggestions([]);
      })
  });

  const insertMention = (user: UserDetailType) => {
    const input = inputRef.current;
    const cursorPos = input.selectionStart;
    const before = comment.slice(0, cursorPos);
    const after = comment.slice(cursorPos);
    const updated = before.replace(/@[\w]{3,}$/, `@${user.userName} `) + after;

    setComment(updated);
    markMentionInserted(user.userName);
    mentions.current[user.userName] = user;

    setAnchorEl(null);
    setSuggestions([]);
    setSelectedIndex(0);

    setTimeout(() => {
      const newCursorPos = before.replace(/@[\w]{3,}$/, `@${user.userName} `).length;
      input.setSelectionRange(newCursorPos, newCursorPos);
      input.focus();
    }, 0);
  };

  const handleKeyDown = (e) => {
    const input = inputRef.current;
    const cursorPos = input.selectionStart;
    const before = comment.slice(0, cursorPos);

    if (e.key === 'Backspace') {
      const mentionMatch = before.match(/@[\w]{3,} $|@[\w]{3,}$/);
      if (mentionMatch) {
        const start = cursorPos - mentionMatch[0].length;
        const username = mentionMatch[0].trim().slice(1);
        const updated = comment.slice(0, start) + comment.slice(cursorPos);
        e.preventDefault();
        setComment(updated);
        let data = mentions.current;
        delete data[username];
        mentions.current = data;
        if (suggestions) {
          setSuggestions([]);
          setAnchorEl(null);
        }

        setTimeout(() => {
          input.setSelectionRange(start, start);
        }, 0);
        return;
      }
    }

    if (anchorEl && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.stopPropagation();
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.stopPropagation();
        e.preventDefault();
        setSelectedIndex((prev) =>
          (prev - 1 + suggestions.length) % suggestions.length
        );
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        const selectedUser = suggestions[selectedIndex];
        if (selectedUser) {
          e.stopPropagation();
          e.preventDefault();
          insertMention(selectedUser);
        }
      } else if (e.key === 'Escape') {
        e.stopPropagation();
        setAnchorEl(null);
      }
    }
  };

  useEffect(() => {
    if (!inputRef.current || !suggestions || suggestions.length === 0) {
      setAnchorEl(null)
    }

    const textarea = inputRef.current;
    const text = comment;
    const cursorPos = textarea.selectionStart;
    const before = text.slice(0, cursorPos);
    const match = before.match(/@[\w]{3,}$/);
    if (!match) return;

    const mirror = mirrorRef.current;
    if (!mirror) return;

    const escaped = before
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/\n/g, '<br/>')
      .replace(/ {2}/g, ' &nbsp;')
      .replace(/@[\w]{3,}$/, `<span id="caret-marker">@${match[0].slice(1)}</span>`);

    mirror.innerHTML = escaped;

    const marker = mirror.querySelector('#caret-marker');
    if (marker) {
      setAnchorEl(marker);
    }
  }, [comment, suggestions]);

  useEffect(() => {
    if (onChange) onChange(comment);
  }, [comment, onChange])

  // Expose comment and mentions via ref
  useImperativeHandle(ref, () => ({
    getData: () => ({
      comment,
      mentionsData: mentions.current,
    }),
    clear: () => {
      setComment('');
      mentions.current = {};
    },
  }));

  return (
    <>
      <TextField
        inputRef={inputRef}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="What is your reply?"
        multiline
        fullWidth
        minRows={4}
        variant="standard"
        slotProps={{
          input: {
            disableUnderline: true,
            sx: {
              [theme.breakpoints.down('md')]: {
                height: '100%',
                '& textarea': {
                  height: '100% !important',
                }
              }
            }
          }
        }}
        sx={{
          [theme.breakpoints.down('md')]: {
            height: '100%',
          }
        }}
      />

      <div
        ref={mirrorRef}
        style={{
          position: 'absolute',
          whiteSpace: 'pre-wrap',
          wordWrap: 'break-word',
          visibility: 'hidden',
          zIndex: -1,
          fontSize: '16px',
          fontFamily: 'inherit',
          lineHeight: 2,
        }}
      />

      <Popover
        open={Boolean(anchorEl) && !!suggestions?.length}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        onKeyDown={handleKeyDown}
      >
        <List dense>
          {suggestions.map((user, index) => (
            <ListItem disablePadding key={user.user_id}>
              <ListItemButton
                selected={index === selectedIndex}
                onClick={() => insertMention(user)}
              >
                <ListItemText primary={`@${user.userName}`} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Popover>
    </>
  );
});
